import { type RefObject, useEffect, useRef } from "react";
import { isMac } from "@/lib/utils";

export type Hotkey = {
	/** Chord (`mod+k`) or two-key sequence (`g f`) or a single key (`c`). */
	keys: string;
	handler: (event: KeyboardEvent) => void;
	/** Fire while typing in an input. `mod+` chords always do. */
	allowInInputs?: boolean;
	enabled?: boolean;
};

const SEQUENCE_TIMEOUT_MS = 900;
const registry = new Set<RefObject<Hotkey[]>>();
let sequenceStart: { key: string; at: number } | null = null;
let installed = false;

const isTyping = (target: EventTarget | null) => {
	const el = target as HTMLElement | null;
	return !!el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName));
};

function matchesChord(event: KeyboardEvent, chord: string) {
	const parts = chord.split("+");
	const key = parts.pop();
	const mods = new Set(parts);
	return (
		event.key.toLowerCase() === key &&
		mods.has("mod") === (isMac ? event.metaKey : event.ctrlKey) &&
		mods.has("shift") === event.shiftKey &&
		mods.has("alt") === event.altKey
	);
}

function fire(event: KeyboardEvent, hotkey: Hotkey) {
	event.preventDefault();
	hotkey.handler(event);
}

function onKeyDown(event: KeyboardEvent) {
	if (event.defaultPrevented || event.isComposing) return;
	const hotkeys = [...registry]
		.reverse()
		.flatMap((ref) => ref.current.filter((h) => h.enabled !== false));
	const typing = isTyping(event.target);
	const key = event.key.toLowerCase();
	const plain = !event.metaKey && !event.ctrlKey && !event.altKey;
	const allowed = (h: Hotkey) => !typing || h.allowInInputs;

	for (const hotkey of hotkeys) {
		if (hotkey.keys.includes("+") && (allowed(hotkey) || hotkey.keys.startsWith("mod+"))) {
			if (matchesChord(event, hotkey.keys)) return fire(event, hotkey);
		}
	}
	if (!plain) return;

	const pending =
		sequenceStart && Date.now() - sequenceStart.at < SEQUENCE_TIMEOUT_MS ? sequenceStart.key : null;
	sequenceStart = null;
	if (pending) {
		const match = hotkeys.find((h) => allowed(h) && h.keys === `${pending} ${key}`);
		if (match) return fire(event, match);
	}
	const single = hotkeys.find((h) => allowed(h) && h.keys === key);
	if (single) return fire(event, single);
	if (!typing && hotkeys.some((h) => h.keys.startsWith(`${key} `)))
		sequenceStart = { key, at: Date.now() };
}

/** Registers global shortcuts for the component's lifetime. Inline arrays are fine (read via ref). */
export function useHotkeys(hotkeys: Hotkey[]) {
	const ref = useRef(hotkeys);
	ref.current = hotkeys;
	useEffect(() => {
		registry.add(ref);
		if (!installed) {
			window.addEventListener("keydown", onKeyDown);
			installed = true;
		}
		return () => {
			registry.delete(ref);
		};
	}, []);
}
